import { Dialog, Transition } from "@headlessui/react";
import { CheckCircle } from "lucide-react";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AuthContext from "../../store/auth-context";
import { automationTestIds } from "../../testing/automationTestIds";
import { consumePostLogoutNotice } from "../../utils/postLogoutNotice";

const PasswordUpdatedModal = ({ isLandingPage = false }) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [notice, setNotice] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const pendingNotice = consumePostLogoutNotice();
    if (pendingNotice?.type === "password_updated") {
      setNotice(pendingNotice);
      setIsOpen(true);
    }
  }, []);

  const closeModal = () => setIsOpen(false);

  const handleSignIn = () => {
    closeModal();
    authContext.chooseSignUp_SignIn(true);
    // The landing page shows the sign-in form inline and deliberately keeps the
    // login modal closed, so only the other layouts need it opened.
    if (!isLandingPage) {
      authContext.setTriggerLoginModuleToggleValue(true);
    }
  };

  return (
    <Transition
      appear
      show={isOpen}
      as={Fragment}
      afterLeave={() => setNotice(null)}
    >
      <Dialog
        as="div"
        className="relative z-30"
        onClose={closeModal}
        id={automationTestIds.passwordUpdatedModal}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="relative w-full max-w-md transform overflow-hidden rounded-[1.25rem] bg-cfSecondary p-8 text-center align-middle shadow-xl transition-all"
                id={automationTestIds.passwordUpdatedModalPanel}
              >
                <button
                  type="button"
                  onClick={closeModal}
                  className="absolute top-4 right-4 text-cfSignInSignUpInputPlaceholderText hover:text-cfSignInSignUpHeadingText focus:outline-none text-xl leading-none"
                  aria-label={t("resetpassword:post_logout_close")}
                  id={automationTestIds.passwordUpdatedModalClose}
                >
                  &#10005;
                </button>

                <div className="flex justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#E8F5EC]">
                    <CheckCircle className="h-9 w-9 text-[#12B76A]" />
                  </div>
                </div>

                <Dialog.Title
                  as="h3"
                  className="mt-6 text-2xl font-semibold leading-7 text-cfSignInSignUpHeadingText"
                  id={automationTestIds.passwordUpdatedModalTitle}
                >
                  {t("resetpassword:post_logout_title")}
                </Dialog.Title>

                <p
                  className="mt-3 text-base leading-6 text-cfSignInSignUpIntroMessageText"
                  id={automationTestIds.passwordUpdatedModalMessage}
                >
                  {t("resetpassword:post_logout_description")}
                </p>

                {notice?.email && (
                  <p
                    className="mt-4 truncate rounded-lg bg-cfSearchContainer px-3 py-2 text-sm font-medium text-cfSignInSignUpInputLabelText"
                    id={automationTestIds.passwordUpdatedModalAccount}
                  >
                    {notice.email}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleSignIn}
                  className="mt-7 h-11 w-full rounded-xl bg-cfButton px-5 text-base font-semibold text-cfSecondary transition-colors duration-200 hover:bg-cfPrimaryDark focus:outline-none"
                  id={automationTestIds.passwordUpdatedModalSignIn}
                >
                  {t("resetpassword:post_logout_sign_in")}
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PasswordUpdatedModal;
